import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MailX, Loader2 } from "lucide-react";
import { api, formatErr } from "../../lib/api";

/**
 * BouncedEmailCard (iter155) — one-tap quarantine of hard-bounced email
 * addresses so future broadcasts skip them. Also lists every currently
 * quarantined address so the operator has visibility.
 */
export default function BouncedEmailCard() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users/bounced");
      setList(data.items || []);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const quarantine = async () => {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/users/quarantine-email", { email: email.trim() });
      toast.success(`${email.trim()} quarantined — future broadcasts skip it.`);
      setEmail("");
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  return (
    <section
      className="border hairline rounded-2xl p-5 sm:p-6 mb-6 bg-white"
      data-testid="admin-bounced-emails"
    >
      <div className="min-w-0">
        <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
          <MailX className="w-3 h-3" /> Bounced emails
        </div>
        <h2 className="font-display text-xl sm:text-2xl tracking-tight">
          Quarantine a bounced address
        </h2>
        <p className="text-xs text-[#666] mt-1 max-w-xl">
          If a broadcast bounces (e.g. someone signed up with a typo email like
          <code className="bg-[#fff4ee] px-1 rounded"> m@gmail.com </code>),
          paste the address here to skip it in every future send. Also
          auto-opts them out of the weekly newsletter.
        </p>
      </div>

      <div className="mt-4 flex gap-2 items-center">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="bounced@example.com"
          className="flex-1 px-3 py-2 border hairline rounded-lg text-sm"
          data-testid="bounced-email-input"
        />
        <button
          type="button"
          disabled={busy || !email.trim()}
          onClick={quarantine}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#ff4500] text-white text-sm font-black uppercase tracking-wider hover:bg-[#e63e00] disabled:opacity-60"
          data-testid="bounced-email-submit"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MailX className="w-4 h-4" />}
          Quarantine
        </button>
      </div>

      {list.length > 0 && (
        <div className="mt-4 border hairline rounded-lg p-3">
          <div className="text-[11px] uppercase tracking-wider font-bold text-[#666] mb-2">
            Currently quarantined ({list.length})
          </div>
          <ul className="text-xs text-[#444] space-y-1 max-h-40 overflow-y-auto">
            {list.map((u) => (
              <li key={u.user_id || u.email} className="flex justify-between gap-2">
                <span className="truncate">{u.email}</span>
                <span className="text-[#999] shrink-0">
                  {u.email_bounced_at
                    ? new Date(u.email_bounced_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })
                    : "—"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
